import { MenuItem, Stack, Typography } from "@mui/material";
import { IFeatureSet } from "@nendaTypes/NendaTypes";
import { t } from "i18next";
import InputSelect from "../../ui-components/input/InputSelect";
import {
  GetFeatureSetsAction,
  getFeatureSetsSelector,
} from "../../store/reducers/offeringsReducer";
import { useSelector } from "react-redux";
import { useEffect } from "react";
import { CustomerPortalState, store } from "../../store";
import {
  GetCollectionsAction,
  selectMediaCollectionsByIds,
} from "../../store/reducers/cmsReducer";

const EMPTY_ARRAY: string[] = [];

const FeatureSetSelector: React.FC<{
  featureSets: IFeatureSet[];
  currentFeatureSets: string[];
  onChange: (featureSetIds: string[]) => void;
}> = ({ featureSets, currentFeatureSets, onChange }) => {
  return (
    <InputSelect
      id="feature-set"
      disabled={true}
      multiple
      value={currentFeatureSets}
      title={t("customerportal.pages.unit.entitlement.select_feature_set", {
        defaultValue: "Select Feature Set",
      })}
      onChange={(e) => {
        const value = e.target.value;
        const ids =
          typeof value === "string"
            ? value
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
            : (value as string[]);

        onChange(ids);
      }}
      variant={"standard"}
      sx={{ fontSize: "1rem", textTransform: "capitalize" }}
    >
      {featureSets.map((featureSet) => (
        <MenuItem value={featureSet._id} key={featureSet._id}>
          {featureSet.name}
        </MenuItem>
      ))}
    </InputSelect>
  );
};

interface EntitlementProps {
  currentFeatureSets: string[];
  onFeatureSetChange: (featureSetIds: string[]) => void;
}

const Entitlement: React.FC<EntitlementProps> = ({
  currentFeatureSets,
  onFeatureSetChange,
}) => {
  const featureSets = useSelector(getFeatureSetsSelector);

  const currentSelectedFeatureSets = featureSets.filter((fs) =>
    currentFeatureSets.includes(fs._id)
  );

  const selectedMediaCollectionIds =
    currentSelectedFeatureSets.length > 0
      ? Array.from(
          new Set(
            currentSelectedFeatureSets.flatMap(
              (fs) => fs.mediaCollections || EMPTY_ARRAY
            )
          )
        )
      : EMPTY_ARRAY;

  const mediaCollections = useSelector((state: CustomerPortalState) =>
    selectMediaCollectionsByIds(state, selectedMediaCollectionIds)
  );

  useEffect(() => {
    store.dispatch(GetFeatureSetsAction());
  }, []);

  useEffect(() => {
    store.dispatch(GetCollectionsAction());
  }, []);

  const handleFeatureSetChange = (ids: string[]) => {
    onFeatureSetChange(ids);
  };

  return (
    <Stack gap={1}>
      <Typography variant="h6">
        {t("customerportal.pages.unit.entitlement.title", {
          defaultValue: "Entitlement",
        })}
      </Typography>

      {featureSets.length > 0 && (
        <FeatureSetSelector
          featureSets={featureSets}
          currentFeatureSets={currentFeatureSets}
          onChange={handleFeatureSetChange}
        />
      )}

      {currentSelectedFeatureSets.length > 0 && mediaCollections.length > 0 && (
        <Typography variant="body2">
          {t("customerportal.pages.unit.entitlement.includes", {
            defaultValue: "Includes:",
          })}{" "}
          {mediaCollections.map((mc) => mc.name).join(", ")}
        </Typography>
      )}
    </Stack>
  );
};

export default Entitlement;
